import { type CSSProperties, useCallback, useState } from 'react';

/**
 * Blocking modal shown when the desktop app version is below min_version.
 * Cannot be dismissed — the user must update to continue.
 *
 * Scenarios:
 * - Update available → "Aktualizovat" button → quitAndInstall()
 * - Update NOT available → "Kontaktujte IT" message + retry button
 */

interface ForceUpdateModalProps {
  requiredVersion: string;
  currentVersion: string;
}

export function ForceUpdateModal({ requiredVersion, currentVersion }: ForceUpdateModalProps) {
  const [retrying, setRetrying] = useState(false);

  const handleUpdate = useCallback(async () => {
    if (!window.zoobook) return;
    try {
      await window.zoobook.invoke('updater:quitAndInstall');
    } catch {
      // Update not downloaded yet — will be handled by retry
    }
  }, []);

  const handleRetry = useCallback(async () => {
    setRetrying(true);
    // Wait a bit for the updater to potentially find the update
    await new Promise((resolve) => setTimeout(resolve, 3000));
    try {
      await window.zoobook?.invoke('updater:quitAndInstall');
    } catch {
      // Still not available
    }
    setRetrying(false);
  }, []);

  return (
    <div style={overlay}>
      <div style={modal}>
        <div style={iconContainer}>
          <svg
            role="img"
            aria-label="Update required"
            width="48"
            height="48"
            viewBox="0 0 24 24"
            fill="none"
            stroke="var(--color-tint-orange)"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <circle cx="12" cy="12" r="10" />
            <line x1="12" y1="8" x2="12" y2="12" />
            <line x1="12" y1="16" x2="12.01" y2="16" />
          </svg>
        </div>

        <h2 style={title}>Vyžadována aktualizace</h2>

        <p style={description}>
          Vaše verze ({currentVersion}) je zastaralá. Pro pokračování je nutná verze{' '}
          {requiredVersion} nebo novější.
        </p>

        <div style={buttonGroup}>
          <button type="button" onClick={handleUpdate} style={primaryButton}>
            Aktualizovat a restartovat
          </button>

          <button type="button" onClick={handleRetry} disabled={retrying} style={secondaryButton}>
            {retrying ? 'Zkouším...' : 'Zkusit znovu'}
          </button>
        </div>

        <p style={helpText}>Pokud aktualizace není dostupná, kontaktujte IT správce vaší zoo.</p>
      </div>
    </div>
  );
}

// ─── Styles ───

const overlay: CSSProperties = {
  position: 'fixed',
  inset: 0,
  zIndex: 99999,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: 'rgba(0, 0, 0, 0.6)',
  backdropFilter: 'blur(4px)',
};

const modal: CSSProperties = {
  background: 'var(--color-bg-primary, #ffffff)',
  borderRadius: 16,
  padding: 32,
  maxWidth: 420,
  width: '90%',
  textAlign: 'center',
  boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
};

const iconContainer: CSSProperties = {
  marginBottom: 16,
};

const title: CSSProperties = {
  fontSize: 'var(--font-size-title2, 22px)',
  fontWeight: 700,
  color: 'var(--color-label-primary, #000)',
  margin: '0 0 8px',
};

const description: CSSProperties = {
  fontSize: 'var(--font-size-body, 15px)',
  color: 'var(--color-label-secondary, #666)',
  lineHeight: 1.5,
  margin: '0 0 24px',
};

const buttonGroup: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 8,
};

const primaryButton: CSSProperties = {
  padding: '12px 24px',
  borderRadius: 10,
  border: 'none',
  background: 'var(--color-tint-blue, #007AFF)',
  color: '#fff',
  fontSize: 'var(--font-size-body, 15px)',
  fontWeight: 600,
  cursor: 'pointer',
};

const secondaryButton: CSSProperties = {
  padding: '10px 24px',
  borderRadius: 10,
  border: '1px solid var(--color-border-opaque, #ddd)',
  background: 'transparent',
  color: 'var(--color-label-secondary, #666)',
  fontSize: 'var(--font-size-caption, 13px)',
  cursor: 'pointer',
};

const helpText: CSSProperties = {
  fontSize: 'var(--font-size-caption, 12px)',
  color: 'var(--color-label-tertiary, #999)',
  marginTop: 16,
  margin: '16px 0 0',
};
